import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { List } from 'immutable';
import moment from 'moment';
import Actions from 'flow/actions';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import {
    selectVideoChatConnected,
    selectVideoChatError,
    selectVideoChatMessages,
    selectVideoChatReconnecting,
} from 'flow/selectors/video-chat-selectors';
import { selectSenderName } from 'flow/selectors/meeting-selectors';
import { sendVideoChatMessage } from 'flow/api-clients/video-chat-client';
import { setActiveMeetingChatOpenStatusAction } from 'flow/actions/meeting-actions';
import Icon from 'flow/components/ui-components/Icon';
import IconButton from 'flow/components/ui-components/IconButton';
import MessageGroupDivider from 'flow/pages/messages/MessageGroupDivider';
import useAutosizeTextArea from 'flow/hooks/useAutosizeTextArea';
import useAppContext from 'flow/hooks/useAppContext';
import { twMerge } from 'tailwind-merge';
import { TypedMap } from 'flow/types/TypedMap';
import { VideoChatMessage } from 'flow/types/VideoChat';
import { sliceUUIDFromUserName } from 'flow/utils/meeting-utils';
import ActivateChatHistoryButton from './ActivateChatHistoryButton';

/**
 * Ephemeral chat panel for the in-meeting side-channel.
 *
 * Rendered for any participant who is not a member of the meeting's
 * chat-service session — this includes external guests (who have no Telavox
 * identity) and logged-in users whose meeting has no chat-service session at
 * all.
 *
 * All messages sent from here are tagged {@code isExternal:true} so internal
 * clients render them, and we render everything we receive (no filtering on
 * the guest side). Styled to match the regular chat-service message list
 * (ChatMessage/MessageList/MessageGroupDivider/MessageInput) even though it
 * can't reuse those components directly — they're wired to chat-service
 * Redux state, threads, reactions and attachments that don't exist here.
 */

type HeaderProps = {
    connected: boolean;
    onClose: () => void;
};

const ChatPanelHeader: React.FC<HeaderProps> = ({ connected, onClose }) => {
    const { i18next } = useAppContext();
    return (
        <header className="flex h-16 w-full items-center justify-center border-b border-gray-700 px-4 font-bold">
            <div className="flex flex-1 justify-start" />
            <div className="min-w-0 truncate">
                {i18next.t('call.canvas.chat.title')}
                {!connected && (
                    <span className="ms-2 text-xs font-normal text-gray-400">
                        {i18next.t('call.canvas.chat.status_disconnected')}
                    </span>
                )}
            </div>
            <div className="flex flex-1 justify-end">
                <button
                    type="button"
                    onClick={onClose}
                    aria-label={i18next.t('call.canvas.chat.close')}
                    className="px-2 text-gray-400 hover:text-white"
                >
                    ×
                </button>
            </div>
        </header>
    );
};

type MessageRowProps = {
    message: TypedMap<VideoChatMessage>;
    showHeader: boolean;
    isOwn: boolean;
};

// Mirrors ExternalChatMessage.tsx's row layout (avatar/name/time/text), the
// established pattern for a chat-service-independent participant with no
// contact/extension to look up — plus grouping consecutive same-sender rows.
const MessageRow: React.FC<MessageRowProps> = ({
    message,
    showHeader,
    isOwn,
}) => {
    const { i18next } = useAppContext();
    const name = message.get('name');
    const displayName = isOwn ? i18next.t('chat.you') : name;
    const text = message.get('text');
    const sent = message.get('ts');

    return (
        <div
            className={twMerge(
                'group relative flex w-full items-start gap-x-2 pe-4',
                showHeader ? 'ps-1 pt-4' : 'ps-15',
            )}
        >
            {showHeader && (
                <div className="ms-2 mt-3">
                    <div className="flex size-8 items-center justify-center rounded-full bg-gray-700 text-xs font-semibold text-gray-300">
                        {name?.charAt(0)?.toUpperCase() ?? '?'}
                    </div>
                </div>
            )}
            <div className="min-w-0 flex-1 rounded-sm p-2 hover:bg-gray-700">
                {showHeader && (
                    <div className="flex items-baseline gap-x-2 pb-1">
                        <span className="min-w-0 truncate text-sm font-semibold text-white">
                            {displayName}
                        </span>
                        <span className="shrink-0 text-xs text-gray-400">
                            {moment(sent).format('HH:mm')}
                        </span>
                    </div>
                )}
                <div className="text-sm break-words whitespace-pre-wrap text-gray-300">
                    {text}
                </div>
            </div>
        </div>
    );
};

type MessageRowData = {
    message: TypedMap<VideoChatMessage>;
    showHeader: boolean;
    isOwn: boolean;
};

type MessageGroupData = {
    dateKey: string;
    rows: MessageRowData[];
};

// Groups by calendar day (for MessageGroupDivider) and by consecutive same
// sender (to only show the avatar/name/time header once per run), matching
// how the regular chat-service message list groups its own rows.
const groupMessages = (
    messages: List<TypedMap<VideoChatMessage>>,
    ownName: string,
): MessageGroupData[] => {
    const groups: MessageGroupData[] = [];
    let prevName: string | null = null;

    messages.forEach((message) => {
        const dateKey = moment(message.get('ts')).format('YYYY-MM-DD');
        const name = message.get('name');
        const isOwn =
            sliceUUIDFromUserName(name) === sliceUUIDFromUserName(ownName);

        const currentGroup = groups[groups.length - 1];
        if (!currentGroup || currentGroup.dateKey !== dateKey) {
            groups.push({ dateKey, rows: [] });
            prevName = null;
        }

        groups[groups.length - 1].rows.push({
            message,
            showHeader: name !== prevName,
            isOwn,
        });
        prevName = name;
    });

    return groups;
};

type MessagesProps = {
    messages: List<TypedMap<VideoChatMessage>>;
    ownName: string;
};

const Messages: React.FC<MessagesProps> = ({ messages, ownName }) => {
    const ref = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (ref.current) {
            ref.current.scrollTop = ref.current.scrollHeight;
        }
    }, [messages]);

    const groups = useMemo(
        () => groupMessages(messages, ownName),
        [messages, ownName],
    );

    return (
        <main
            ref={ref}
            className="scrollbar flex-1 pt-6"
            style={{ overflowAnchor: 'none' }}
        >
            <div className="relative flex flex-col items-center">
                {groups.map((group) => (
                    <div key={group.dateKey} className="w-full max-w-2xl">
                        <MessageGroupDivider
                            date={group.dateKey}
                            videoDark={true}
                        />
                        {group.rows.map(({ message, showHeader, isOwn }) => (
                            <MessageRow
                                key={message.get('id')}
                                message={message}
                                showHeader={showHeader}
                                isOwn={isOwn}
                            />
                        ))}
                    </div>
                ))}
            </div>
        </main>
    );
};

const ERROR_AUTO_DISMISS_MS = 5000;

const ErrorBanner: React.FC<{ reason: string }> = ({ reason }) => {
    const dispatch = useAppDispatch();
    const { i18next } = useAppContext();
    const clear = useCallback(() => {
        dispatch({ type: Actions.VIDEO_CHAT_ERROR_CLEARED });
    }, [dispatch]);
    useEffect(() => {
        const handle = setTimeout(clear, ERROR_AUTO_DISMISS_MS);
        return () => clearTimeout(handle);
    }, [clear, reason]);
    const text =
        reason === 'rate_limited'
            ? i18next.t('call.canvas.chat.error_rate_limited')
            : i18next.t('call.canvas.chat.error_generic', { reason });
    return (
        <div
            role="alert"
            className="flex items-center justify-between border-b border-amber-700 bg-amber-900/40 px-3 py-2 text-sm text-amber-200"
        >
            <span>{text}</span>
            <button
                type="button"
                onClick={clear}
                aria-label={i18next.t('call.canvas.chat.dismiss_error')}
                className="px-2 text-amber-200 hover:text-amber-100"
            >
                ×
            </button>
        </div>
    );
};

// Persistent (not auto-dismissed) notice for the guest panel: the relay is
// the guest's only chat, so when it's not connected we surface why messages
// can't be sent. Distinct from ErrorBanner, which reports transient,
// dismissable server errors.
const DisconnectedBanner: React.FC<{ reconnecting: boolean }> = ({
    reconnecting,
}) => {
    const { i18next } = useAppContext();
    return (
        <div
            role="alert"
            className="flex items-center border-b border-amber-700 bg-amber-900/40 px-3 py-2 text-sm text-amber-200"
        >
            <span>
                {reconnecting
                    ? i18next.t('call.canvas.chat.reconnecting_banner')
                    : i18next.t('call.canvas.chat.disconnected_banner')}
            </span>
        </div>
    );
};

const InputBox: React.FC = () => {
    const [text, setText] = useState('');
    const textAreaRef = useRef<HTMLTextAreaElement>(null);
    const dispatch = useAppDispatch();
    const { i18next } = useAppContext();
    const connected = useAppSelector(selectVideoChatConnected);
    // Everything sent from this panel is tagged isExternal:true. The ephemeral
    // panel is only rendered for participants who are NOT members of the
    // meeting's chat-service session (MeetingVideoCanvas: memberInChat ?
    // VideoChatSession : VideoChatEphemeral), so it never dual-writes to
    // chat-service — the relay is the only channel by which these messages
    // reach the members' merged panel, which renders isExternal:true and drops
    // isExternal:false as redundant chat-service echoes. Keying on identity was
    // wrong: an internal user who joins a 1:1 they aren't a chat member of
    // would send false and silently never reach the original participants.
    // Chat-service membership, not Telavox identity, is what matters here.
    const isExternal = true;
    const trimmed = text.trim();
    const disabled = !connected || trimmed.length === 0;
    const send = useCallback(() => {
        if (!connected || !trimmed) {
            return;
        }
        dispatch(sendVideoChatMessage(trimmed, isExternal));
        setText('');
    }, [connected, dispatch, isExternal, trimmed]);
    useAutosizeTextArea(textAreaRef, text);
    return (
        <div className="relative flex w-full max-w-2xl items-start gap-x-1 bg-gray-700 p-2 first:rounded-t-sm last:rounded-b-sm sm:gap-x-3 sm:p-3">
            <textarea
                ref={textAreaRef}
                rows={1}
                disabled={!connected}
                className="h-fit max-h-24 w-full min-w-0 flex-1 resize-none overflow-y-scroll bg-transparent pt-1 text-base text-wrap text-white placeholder:truncate placeholder:text-gray-400 focus:border-transparent focus:ring-0 focus:outline-hidden disabled:cursor-not-allowed"
                aria-label={i18next.t('call.canvas.chat.placeholder')}
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        send();
                    }
                }}
                placeholder={
                    connected
                        ? i18next.t('call.canvas.chat.placeholder')
                        : i18next.t('call.canvas.chat.placeholder_disconnected')
                }
            />
            <IconButton
                onClick={send}
                disabled={disabled}
                tooltipLabel={i18next.t('call.canvas.chat.send')}
                className={twMerge(
                    'size-8 rounded-sm text-white',
                    disabled
                        ? 'hover:darken-0 bg-gray-300 text-gray-400'
                        : 'bg-green hover:!brighten-10',
                )}
            >
                <Icon iconKey="send_message" />
            </IconButton>
        </div>
    );
};

const VideoChatEphemeral: React.FC = () => {
    const dispatch = useAppDispatch();
    const messages = useAppSelector(selectVideoChatMessages);
    const connected = useAppSelector(selectVideoChatConnected);
    const reconnecting = useAppSelector(selectVideoChatReconnecting);
    const errorMessage = useAppSelector(selectVideoChatError);
    const ownName = useAppSelector(selectSenderName);

    const close = useCallback(() => {
        dispatch(setActiveMeetingChatOpenStatusAction(false));
    }, [dispatch]);

    // Computed once per mount rather than on every render — only the
    // open-time value matters, since it's just an editable default.
    const defaultMeetingName = useMemo(
        () => `${moment().format('MMMM D HH:mm')} meeting`,
        [],
    );

    return (
        <div className="flex h-full w-full flex-col overflow-hidden bg-gray-800 text-white">
            <ChatPanelHeader connected={connected} onClose={close} />
            {errorMessage ? (
                <ErrorBanner reason={errorMessage} />
            ) : (
                !connected && <DisconnectedBanner reconnecting={reconnecting} />
            )}
            <Messages messages={messages} ownName={ownName} />
            {/* The panel clips, so the full-width button keeps the footer's
                side padding, which also leaves room for its focus ring. */}
            <div className="px-4">
                <ActivateChatHistoryButton
                    meetingName={defaultMeetingName}
                    videoDark={true}
                />
            </div>
            <footer className="flex flex-col items-center bg-gray-800 px-4 pb-3">
                <InputBox />
            </footer>
        </div>
    );
};

export default VideoChatEphemeral;
